import { atomicGameStorage } from '@/lib/storage/AtomicGameStorage';
import { sharedSequenceStateManager } from './SharedSequenceStateManager';
import { directCookieAccess } from '@/lib/storage/StorageAdapter';
import { modeAwareStorage } from '@/lib/managers/ModeAwareStorageManager';

type GameMode = 'daily' | 'practice';

interface OverlayStateSnapshot {
  mode: GameMode;
  timestamp: string;
  
  // Storage state
  hasAtomicState: boolean;
  atomicState: any;
  
  // Flags
  inProgressOverlayShown: boolean;
  gameCompletionState: boolean;
  immediateCompletion: boolean;
  redirectToWelcome: boolean;
  practicePlayAgain: boolean;
  fromWelcomePage: boolean;
  isSharedSequence: boolean;
  gameSessionActive: boolean;
  
  // Page state
  isPageReload: boolean;
  sessionLoaded: boolean;
  
  // Decisions
  showInProgress: boolean;
  showGameCompletion: boolean;
  shouldRedirectToWelcome: boolean;
  showInstructions: boolean;
  showStreak: boolean;
}

/**
 * OverlayStateCoordinator - Single Source of Truth for Overlay Decisions
 * 
 * This coordinator eliminates race conditions by:
 * 1. Capturing ALL relevant state in a single atomic snapshot
 * 2. Making ALL decisions based on this snapshot
 * 3. Preventing duplicate reads and timing issues
 */
export class OverlayStateCoordinator {
  private static lastSnapshot: OverlayStateSnapshot | null = null;
  private static correlationId = 0;

  /**
   * Capture complete state snapshot - this is the ONLY place that reads storage
   */
  static captureStateSnapshot(mode: GameMode): OverlayStateSnapshot {
    const correlationId = ++this.correlationId;
    const timestamp = new Date().toISOString();
    
    console.log(`[OverlayCoordinator-${correlationId}] Capturing state snapshot for ${mode}`, { timestamp });

    // 1. Read atomic storage ONCE
    const atomicState = atomicGameStorage.read(mode);
    const hasAtomicState = !!atomicState;

    // 2. Read all flags ONCE
    // CRITICAL FIX: Check both cookie AND sessionStorage for in-progress flag
    const prefix = mode === 'practice' ? 'babblegram_practice_' : 'babblegram_daily_';
    const inProgressCookie = modeAwareStorage.getCookie('in_progress_overlay_shown') === 'true';
    const inProgressSession = sessionStorage.getItem(`${prefix}in_progress_overlay_shown_this_session`) === 'true';
    const inProgressOverlayShown = inProgressCookie || inProgressSession;
    
    const gameCompletionState = modeAwareStorage.getCookie('game_completion_state') === 'true';
    const immediateCompletion = modeAwareStorage.getCookie('immediate_completion') === 'true';
    const redirectToWelcome = modeAwareStorage.getCookie('redirect_to_welcome') === 'true';
    const practicePlayAgain = localStorage.getItem('babblegram_practice_mode_play_again') === 'true';
    const fromWelcomeLS = localStorage.getItem('from_welcome_page') === 'true';
    const fromWelcomeSS = sessionStorage.getItem('from_welcome_page') === 'true';
    const fromWelcomePage = fromWelcomeLS || fromWelcomeSS;
    const isSharedSequence = sharedSequenceStateManager.isSharedSequenceActive();
    
    // CRITICAL FIX: Track if user has an active game session
    const gameSessionActive = sessionStorage.getItem('babblegram_game_session_active') === 'true';

    // 3. Read page state ONCE (without consuming)
    const sessionLoaded = sessionStorage.getItem('babblegram_page_loaded') === 'true';
    const isPageReload = !sessionLoaded;

    const snapshot: OverlayStateSnapshot = {
      mode,
      timestamp,
      hasAtomicState,
      atomicState,
      inProgressOverlayShown,
      gameCompletionState,
      immediateCompletion,
      redirectToWelcome,
      practicePlayAgain,
      fromWelcomePage,
      isSharedSequence,
      gameSessionActive,
      isPageReload,
      sessionLoaded,
      showInProgress: false,
      showGameCompletion: false,
      shouldRedirectToWelcome: false,
      showInstructions: false,
      showStreak: false
    };

    console.log(`[OverlayCoordinator-${correlationId}] State snapshot captured:`, {
      mode,
      hasAtomicState,
      inProgressOverlayShown,
      inProgressCookie,
      inProgressSession,
      gameCompletionState,
      isPageReload,
      fromWelcomePage,
      isSharedSequence,
      practicePlayAgain,
      gameSessionActive,
      currentWord: atomicState?.currentWord,
      gameStatus: atomicState?.gameStatus,
      timestamp
    });

    this.lastSnapshot = snapshot;
    return snapshot;
  }

  /**
   * Make overlay decision based on snapshot - NO storage reads here
   */
  static determineOverlayState(snapshot: OverlayStateSnapshot): OverlayStateSnapshot {
    const correlationId = this.correlationId;
    
    console.log(`[OverlayCoordinator-${correlationId}] Determining overlay state from snapshot`, {
      mode: snapshot.mode,
      timestamp: snapshot.timestamp
    });

    // Priority 1: Shared sequence - no overlays
    if (snapshot.isSharedSequence) {
      console.log(`[OverlayCoordinator-${correlationId}] Shared sequence active, suppressing all overlays`);
      snapshot.showInProgress = false;
      snapshot.showGameCompletion = false;
      snapshot.shouldRedirectToWelcome = false;
      return snapshot;
    }

    // Priority 2: Practice Play Again - no overlays
    if (snapshot.practicePlayAgain) {
      console.log(`[OverlayCoordinator-${correlationId}] Practice Play Again active, suppressing overlays`);
      snapshot.showInProgress = false;
      snapshot.showGameCompletion = false;
      snapshot.shouldRedirectToWelcome = false;
      return snapshot;
    }

    // Priority 3: Coming from welcome page - no redirect
    if (snapshot.fromWelcomePage) {
      console.log(`[OverlayCoordinator-${correlationId}] Coming from welcome page, suppressing redirect`);
      snapshot.shouldRedirectToWelcome = false;
      snapshot.showInProgress = false;
      return snapshot;
    }

    // Priority 4: Explicit redirect cookie
    if (snapshot.redirectToWelcome) {
      console.log(`[OverlayCoordinator-${correlationId}] Explicit redirect cookie found`);
      snapshot.shouldRedirectToWelcome = true;
      snapshot.showInProgress = false;
      snapshot.showGameCompletion = false;
      return snapshot;
    }

    // Priority 5: Game completion state
    if (snapshot.gameCompletionState) {
      console.log(`[OverlayCoordinator-${correlationId}] Game completion state found`);
      snapshot.showGameCompletion = true;
      snapshot.showInProgress = false;
      return snapshot;
    }

    // Priority 6: Immediate completion
    if (snapshot.immediateCompletion) {
      console.log(`[OverlayCoordinator-${correlationId}] Immediate completion flag set`);
      snapshot.showGameCompletion = true;
      snapshot.showInProgress = false;
      return snapshot;
    }

    // Priority 7: Check for in-progress game on page reload
    // CRITICAL FIX: Only redirect if user doesn't have an active game session
    if (snapshot.isPageReload && snapshot.hasAtomicState && !snapshot.gameSessionActive) {
      const state = snapshot.atomicState;
      const hasInProgressGame = state.gameStatus === 'playing' && state.currentWord && state.currentLevel;
      
      console.log(`[OverlayCoordinator-${correlationId}] Page reload with game state check:`, {
        hasInProgressGame,
        gameSessionActive: snapshot.gameSessionActive,
        gameStatus: state.gameStatus,
        currentWord: state.currentWord,
        currentLevel: state.currentLevel
      });

      if (hasInProgressGame) {
        // On first visit/reload with in-progress game, redirect to welcome
        // But NOT if user already has an active session
        console.log(`[OverlayCoordinator-${correlationId}] First visit with in-progress game, redirecting to welcome`);
        snapshot.shouldRedirectToWelcome = true;
        snapshot.showInProgress = false;
        return snapshot;
      }
    } else if (snapshot.isPageReload && snapshot.gameSessionActive) {
      console.log(`[OverlayCoordinator-${correlationId}] Page reload but active session exists, not redirecting`);
    }

    // Priority 8: Show in-progress overlay if not already shown and there's a game
    if (!snapshot.inProgressOverlayShown && snapshot.hasAtomicState) {
      const state = snapshot.atomicState;
      const hasInProgressGame = state.gameStatus === 'playing' && state.currentWord;
      
      if (hasInProgressGame) {
        console.log(`[OverlayCoordinator-${correlationId}] In-progress game found, showing overlay`);
        snapshot.showInProgress = true;
        return snapshot;
      }
    }

    // Default: no overlays
    console.log(`[OverlayCoordinator-${correlationId}] No overlay conditions met, using defaults`);
    return snapshot;
  }

  /**
   * Mark that the page has been loaded (call once after initialization)
   */
  static markPageAsLoaded(): void {
    try {
      sessionStorage.setItem('babblegram_page_loaded', 'true');
      console.log('[OverlayCoordinator] Page marked as loaded');
    } catch (error) {
      console.error('[OverlayCoordinator] Error marking page as loaded:', error);
    }
  }

  /**
   * Get the last snapshot for debugging
   */
  static getLastSnapshot(): OverlayStateSnapshot | null {
    return this.lastSnapshot;
  }

  /**
   * Mark game session as active (call when user starts/continues playing)
   */
  static markSessionActive(): void {
    try {
      sessionStorage.setItem('babblegram_game_session_active', 'true');
      console.log('[OverlayCoordinator] Game session marked as active');
    } catch (error) {
      console.error('[OverlayCoordinator] Error marking session active:', error);
    }
  }

  /**
   * Clear session state (call on logout/reset)
   */
  static clearSessionState(): void {
    try {
      sessionStorage.removeItem('babblegram_page_loaded');
      sessionStorage.removeItem('from_welcome_page');
      sessionStorage.removeItem('babblegram_game_session_active');
      sessionStorage.removeItem('babblegram_daily_in_progress_overlay_shown_this_session');
      sessionStorage.removeItem('babblegram_practice_in_progress_overlay_shown_this_session');
      console.log('[OverlayCoordinator] Session state cleared');
    } catch (error) {
      console.error('[OverlayCoordinator] Error clearing session state:', error);
    }
  }
}
